/**
 * Where the auth state and the router meet.
 *
 * The router is not mounted until the session question is settled. That is deliberate: mounting it
 * while a stored refresh token is still being exchanged would run the guard against "anonymous"
 * and bounce a signed-in person to sign-in on every reload — the bug this ordering exists to
 * prevent.
 */

import { RouterProvider } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ToastProvider } from "./components/toast";
import { AuthProvider, useAuth } from "./lib/auth/auth-context";
import { buildRouter } from "./router";

function RoutedApp() {
  const auth = useAuth();
  // One router for the app's lifetime. A lazy `useState` initialiser rather than `useMemo`,
  // because "run exactly once" is a guarantee `useState` makes and `useMemo` does not.
  const [router] = useState(() => buildRouter(auth));

  // Signing in or out changes what every guard would decide, so the matched routes have to be
  // re-evaluated. Without this a sign-out leaves the last screen on display.
  useEffect(() => {
    void router.invalidate();
  }, [router, auth.status]);

  if (auth.status === "restoring") {
    return <div className="centered-notice">Restoring your session…</div>;
  }

  return <RouterProvider router={router} context={{ auth }} />;
}

export function App() {
  return (
    <AuthProvider>
      {/* Outside the router, so a toast survives the navigation that triggered it — deleting a
          note reports "Deleted." on the screen you land on, not on the one that is unmounting. */}
      <ToastProvider>
        <RoutedApp />
      </ToastProvider>
    </AuthProvider>
  );
}
